import { observer }     from 'mobx-react-lite';
import View             from '@widesk-ui/components/View';
import Alert            from '@widesk-ui/components/Alert';
import Button           from '@widesk-ui/components/Button';
import useDesignTokens  from '@/hooks/useDesignTokens';
import leftSidebarStore from '@/TokamapEditor/stores/leftSidebarStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import planImportStore  from '@/TokamapEditor/planImport/planImportStore';

const EARTH_RADIUS_M = 6378137;

const Figure = (props: { label: string; value: string }) => {
	const tokens = useDesignTokens();

	return (
		<View flex gap={2} style={{ padding: '10px 12px', borderRadius: 8, background: tokens.surface.subtle }}>
			<View size={12} color={tokens.text.secondary}>{props.label}</View>
			<View size={16} style={{ fontWeight: 600 }}>{props.value}</View>
		</View>
	);
};

const Placement = observer(() => {
	const tokens = useDesignTokens();
	const { preview, placement, busy, error, importedCount } = planImportStore;

	const georeferenced = !!preview?.isGeoreferenced;
	const metersPerUnit = placement ? Math.cos(Math.atan(Math.sinh(placement.center[1] / EARTH_RADIUS_M))) : 1;

	const backToReview = () => {
		planImportStore.reopenReview();
		leftSidebarStore.close();
	};

	return (
		<View p="md" gap="md">
			<Alert
				type={georeferenced ? 'success' : 'warning'}
				showIcon
				message={georeferenced
					? 'Plan géolocalisé : posé à son emplacement réel.'
					: 'Plan non géolocalisé : calez-le à la main sur la carte.'}
			/>

			{placement && preview && (
				<View row gap={8}>
					<Figure label="Rotation" value={`${Math.round((placement.rotation * 180) / Math.PI)}°`} />
					<Figure
						label="Taille sur la carte"
						value={`${Math.round(placement.scale * metersPerUnit * preview.width)} × ${Math.round(placement.scale * metersPerUnit * preview.height)} m`}
					/>
				</View>
			)}

			{!placement && (
				<Alert type="warning" showIcon message="Le plan n’est plus sur la carte : reposez-le avant de générer." />
			)}

			<View gap={8}>
				<Button onClick={() => planImportStore.placeBackground(true)}>Recentrer sur la vue</Button>
				<Button onClick={() => void planImportStore.drawBackgroundByHand()}>Tracer à la main</Button>
			</View>

			<Button onClick={backToReview}>Revenir à la vérification</Button>

			{error && <Alert type="error" showIcon message={error} />}

			<Button
				type="primary"
				size="large"
				loading={busy}
				disabled={!placement}
				onClick={() => void planImportStore.generate()}
			>
				{busy ? 'Génération en cours…' : `Générer ${importedCount} forme(s)`}
			</Button>

			<View size={12} color={tokens.text.secondary}>
				Les formes et leurs POI sont créés sur la carte « {currentProjectEditorStore.get.currentMap.label} ». Un POI déjà présent est mis à jour plutôt que recréé.
			</View>
		</View>
	);
});

const Report = observer(() => {
	const tokens = useDesignTokens();
	const report = planImportStore.report;
	if (!report) return null;

	return (
		<View p="md" gap="md">
			<View row gap={8}>
				<Figure label="POI créés" value={String(report.nbInsertions)} />
				<Figure label="POI mis à jour" value={String(report.nbUpdates)} />
			</View>
			<View row gap={8}>
				<Figure label="Numéros en double" value={String(report.nbDuplicates)} />
				<Figure label="POI verrouillés" value={String(report.nbLocked)} />
			</View>

			{0 < report.duplicates.length && (
				<Alert
					type="warning"
					showIcon
					message="Numéros présents deux fois"
					description={(
						<View gap={2}>
							{report.duplicates.map(duplicate => (
								<View key={`${duplicate.category}-${duplicate.number}`} size={13}>
									« {duplicate.number} » dans « {duplicate.category} » : un seul POI créé
								</View>
							))}
						</View>
					)}
				/>
			)}

			{0 < report.nbLocked && (
				<Alert
					type="warning"
					showIcon
					message={`${report.nbLocked} POI créés verrouillés`}
					description="Ils dépassent la limite de POI de l’abonnement."
				/>
			)}

			<View row gap={8}>
				<Button style={{ flex: 1 }} onClick={() => leftSidebarStore.close()}>Fermer</Button>
				<Button
					type="primary"
					style={{ flex: 1 }}
					onClick={() => {
						leftSidebarStore.close();
						planImportStore.open();
					}}
				>
					Importer un autre plan
				</Button>
			</View>

			<View size={12} color={tokens.text.secondary}>
				Une analyse générée ne peut plus l’être une seconde fois : un réimport repart d’un nouveau fichier.
			</View>
		</View>
	);
});

const PlanPlacementSidebar = observer(() => ('report' === planImportStore.step ? <Report /> : <Placement />));

export default PlanPlacementSidebar;

export const PlanPlacementTitle = observer(() => <>{'report' === planImportStore.step ? 'Formes générées' : 'Placer le plan'}</>);
